// https://github.com/MatthewHerbst/react-to-print#readme
import { type FC } from 'react';

// MUI
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import Box from '@mui/material/Box';

// screen
import { type NutritionMenuModel } from 'modules/map/components/nutrition-menu/types';
import PdfPrintTitle from 'modules/map/components/pdf-print-form/PdfPrintTitle';
import PrintThemeWrapper from 'modules/map/components/pdf-print-form/PrintThemeWrapper';
import {
  renderCell,
  sharedPrintTableStyles,
  SMALL_FONT_SIZE,
} from 'modules/map/components/pdf-print-form/utils';
import PdfTableHeader from 'modules/map/components/pdf-print-form/PdfTableHeader';
import SixDaysFormFooter from 'modules/map/components/nutrition-menu/pdf/six-days/SixDaysFormFooter';
import SixDaysMenu from 'modules/map/components/nutrition-menu/pdf/six-days/SixDaysMenu';
import FiveDaysMenu from 'modules/map/components/nutrition-menu/pdf/five-days/FiveDaysMenu';
import FiveDaysFormFooter from 'modules/map/components/nutrition-menu/pdf/five-days/FiveDaysFormFooter';

const tableHeaders = (isNutritionMenu: boolean, shouldShowAfternoonMeals: boolean) => {
  const withCalories = [
    renderCell({ label: 'map.weekDayAndDate', rowSpan: 2 }),
    renderCell({ label: 'map.menuUppercase', rowSpan: 2, width: '16%' }),
    renderCell({
      label: 'map.dishQuantity',
      rowSpan: 1,
      colSpan: 2,
      width: '6%',
    }),
    renderCell({ label: 'map.energyLunch', rowSpan: 2, width: '8%' }),
    renderCell({ label: 'map.protein', rowSpan: 2, width: '8%' }),
    renderCell({ label: 'map.lipid', rowSpan: 2, width: '8%' }),
    renderCell({ label: 'map.gluxit', rowSpan: 2, width: '8%' }),
    shouldShowAfternoonMeals
      ? renderCell({
          label: 'map.afternoonUppercase',
          rowSpan: 2,
        })
      : null,
    shouldShowAfternoonMeals
      ? renderCell({
          label: 'map.energyAfternoon',
          rowSpan: 2,
          width: '6%',
        })
      : null,
    renderCell({ label: 'map.noteUppercase', rowSpan: 2, width: '8%' }),
  ];

  const withoutCalories = [
    renderCell({ label: 'map.weekDayAndDate', rowSpan: 2, width: '10%' }),
    renderCell({ label: 'map.menuUppercase', rowSpan: 2, width: '25%' }),
    renderCell({
      label: 'map.dishQuantity',
      rowSpan: 1,
      colSpan: 2,
      width: '30%',
    }),
    shouldShowAfternoonMeals
      ? renderCell({
          label: 'map.afternoonUppercase',
          rowSpan: 2,
          width: '15%',
        })
      : null,
    renderCell({ label: 'map.noteUppercase', rowSpan: 2, width: '15%' }),
  ];

  return isNutritionMenu ? withCalories : withoutCalories;
};

interface PdfNutritionMenuProp {
  data: NutritionMenuModel;
  customerType?: string;
}

const PdfNutritionMenu: FC<PdfNutritionMenuProp> = ({ data, customerType }) => {
  const info = data?.structuredData?.data?.info;

  const caloriesIncluded = data.structuredData.metadata.isNutritionMenu;
  const shouldShowAfternoonMeals = data?.structuredData.metadata.hasAfternoon;

  if (!customerType) {
    throw Error('customerType is expected');
  }

  const decisionNumber =
    (customerType || '') === 'TH' ? 'Quyết định số 2195/QĐ-BGDĐT' : 'Quyết định số 4280/QĐ-BGDĐT';

  const ingredientsQuantities = () => {
    const width = caloriesIncluded ? '10%' : '15%';

    return [
      renderCell({ label: 'map.rawUnit', width, rowSpan: 1 }),
      renderCell({ label: 'map.cookedUnit', width, rowSpan: 1 }),
    ];
  };

  const days = data.structuredData?.data?.items || [];
  const tableHeight =
    days.length === 6 ? '74vh' : days.length === 5 && caloriesIncluded ? '77vh' : '78vh';

  return (
    <PrintThemeWrapper>
      <TableContainer className="px-2 tablet:px-4" sx={sharedPrintTableStyles}>
        <PdfPrintTitle
          title={info.title || ''}
          subTitle={`(Thực đơn được xây dựng dựa trên Bảng thành phần thực phẩm Việt Nam do Bộ Y tế và Viện Dinh Dưỡng ban hành năm 2017 Và ${decisionNumber} ban hành năm 2022)`}
          week={info.weekTitle}
        />
        <Table
          sx={{
            borderCollapse: 'separate',
            height: tableHeight,
          }}
        >
          <PdfTableHeader
            firstRow={tableHeaders(caloriesIncluded, shouldShowAfternoonMeals).filter(
              (item) => item !== null
            )}
            secondRow={ingredientsQuantities()}
            firstRowCustomStyle={{ fontSize: SMALL_FONT_SIZE }}
            secondRowCustomStyle={{ fontSize: SMALL_FONT_SIZE }}
          />
          {days.length === 5 && (
            <FiveDaysMenu
              days={data.structuredData?.data?.items || []}
              caloriesIncluded={caloriesIncluded}
              shouldShowAfternoonMeals={shouldShowAfternoonMeals}
            />
          )}
          {days.length === 6 && (
            <SixDaysMenu
              days={data.structuredData?.data?.items || []}
              caloriesIncluded={caloriesIncluded}
              shouldShowAfternoonMeals={shouldShowAfternoonMeals}
            />
          )}
        </Table>
        <Box sx={{ height: '14vh' }}>
          {days.length === 5 && (
            <FiveDaysFormFooter caloriesIncluded={caloriesIncluded} footerInfo={info} />
          )}
          {days.length === 6 && (
            <SixDaysFormFooter caloriesIncluded={caloriesIncluded} footerInfo={info} />
          )}
        </Box>
      </TableContainer>
    </PrintThemeWrapper>
  );
};

export default PdfNutritionMenu;
